import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { UserIdentity } from '../models/user-identity';

@Injectable({ providedIn: 'root' })
export class StorageService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly identityKey = 'identity';
  private readonly passwordKey = 'changePasswordUi';

  readonly currentIdentity = signal<UserIdentity | null>(this.getIdentity());
  readonly isLoggedIn = computed(() => this.isAuthenticated());

  getItem<T>(key: string, storage: Storage | null = this.localStorage()): T | null {
    if (!storage) return null;
    try {
      const raw = storage.getItem(key);
      return raw === null ? null : JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  setItem<T>(key: string, value: T, storage: Storage | null = this.localStorage()): void {
    if (!storage) return;
    try { storage.setItem(key, JSON.stringify(value)); } catch { /* Storage may be unavailable. */ }
  }

  removeItem(key: string, storage: Storage | null = this.localStorage()): void {
    if (!storage) return;
    try { storage.removeItem(key); } catch { /* Storage may be unavailable. */ }
  }

  setIdentity(identity: UserIdentity): void {
    this.setItem(this.identityKey, identity);
    this.currentIdentity.set(identity);
  }

  getIdentity(): UserIdentity | null {
    return this.getItem<UserIdentity>(this.identityKey);
  }

  removeIdentity(): void {
    this.removeItem(this.identityKey);
    this.removeVisitedURL();
    this.currentIdentity.set(null);
  }

  setUser<T>(user: T): void { this.setItem('user', user); }
  getUser<T = unknown>(): T | null { return this.getItem<T>('user'); }
  removeUser(): void { this.removeItem('user'); }
  getUserId(): string | number | null { return this.currentIdentity()?.userId ?? null; }
  getToken(): string | null { return this.currentIdentity()?.accessToken ?? null; }
  isAuthenticated(): boolean { return !!this.currentIdentity()?.accessToken; }
  getUserName(): string | null { return this.currentIdentity()?.userName ?? null; }
  getEnvironment(): string | null { return this.currentIdentity()?.env ?? null; }
  getDeptId(): number { return Number(this.currentIdentity()?.deptId ?? 0); }

  getRoles(): string[] {
    const identityRoles = this.currentIdentity()?.roles ?? this.currentIdentity()?.authorities;
    const savedUser = this.getUser<{ roles?: string | string[] }>();
    const raw = identityRoles ?? savedUser?.roles;
    if (Array.isArray(raw)) return raw;
    return typeof raw === 'string' ? raw.split(',').map(role => role.trim()).filter(Boolean) : [];
  }

  setChangePasswordToken(): void {
    const userId = this.getUserId();
    if (userId !== null) this.setItem(this.passwordKey, String(userId), this.sessionStorage());
  }

  isChangePasswordActive(): boolean {
    const value = this.getItem<string>(this.passwordKey, this.sessionStorage());
    return value !== null && this.getUserId() !== null && value === String(this.getUserId());
  }

  // Backward-compatible name used in the previous RMS project.
  isActiveChangePasswordUi(): boolean { return this.isChangePasswordActive(); }
  removeChangePasswordToken(): void { this.removeItem(this.passwordKey, this.sessionStorage()); }
  setVisitedURL(url: string): void { this.setItem('visitedURL', url); }
  getVisitedURL(): string | null { return this.getItem<string>('visitedURL'); }
  removeVisitedURL(): void { this.removeItem('visitedURL'); }
  removeTabData(): void { this.removeItem('rms-open-tabs'); }

  // Deliberately clear only RMS auth keys; never wipe unrelated browser data.
  clearAll(): void {
    this.removeIdentity();
    this.removeUser();
    this.removeItem('token');
    this.removeTabData();
    this.removeChangePasswordToken();
  }

  private localStorage(): Storage | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try { return window.localStorage; } catch { return null; }
  }

  private sessionStorage(): Storage | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try { return window.sessionStorage; } catch { return null; }
  }
}
